import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { StatusBadge } from "@/components/StatusBadge";
import { relativeTime, formatDateTime } from "@/lib/format";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export default function SuperSystemLogs() {
    const { t } = useTranslation();
    const [jobs, setJobs] = useState([]);
    const [statusF, setStatusF] = useState("all");
    const [retrying, setRetrying] = useState(null);

    const load = async () => {
        const params = new URLSearchParams();
        if (statusF !== "all") params.set("status", statusF);
        params.set("limit", "300");
        const { data } = await api.get(`/platform/sync-jobs?${params}`);
        setJobs(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusF]);

    const retry = async (id) => {
        setRetrying(id);
        try { const { data } = await api.post(`/platform/sync-jobs/${id}/retry`); toast.success(`Retry: ${data.status}`); await load(); }
        catch { toast.error("Retry failed"); }
        finally { setRetrying(null); }
    };

    const counts = {
        all: jobs.length,
        success: jobs.filter((j) => j.status === "success").length,
        failed: jobs.filter((j) => j.status === "failed").length,
    };

    return (
        <>
            <Topbar title={t("super_admin.system_logs_title")} subtitle="" />
            <div className="flex-1 overflow-auto p-6">
                <div className="mb-3 flex gap-1">
                    {["all", "success", "failed"].map((s) => (
                        <button
                            key={s}
                            onClick={() => setStatusF(s)}
                            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[12px] font-medium capitalize ${statusF === s ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:bg-muted/60"}`}
                            data-testid={`syslog-filter-${s}`}
                        >
                            {s}
                            <span className={`rounded-full px-1.5 text-[10px] ${statusF === s ? "bg-primary-foreground/20" : "bg-muted text-muted-foreground"}`}>{counts[s]}</span>
                        </button>
                    ))}
                </div>

                <div className="akm-card overflow-hidden">
                    <table className="akm-table w-full">
                        <thead><tr><th>Time</th><th>Channel</th><th>Status</th><th>Synced</th><th>Duration</th><th>Error</th><th>Retried</th><th></th></tr></thead>
                        <tbody>
                            {jobs.map((j) => (
                                <tr key={j.id}>
                                    <td className="font-mono text-[11px] text-muted-foreground" title={formatDateTime(j.created_at)}>{relativeTime(j.created_at)}</td>
                                    <td className="font-medium capitalize">{j.channel}</td>
                                    <td><StatusBadge status={j.status === "success" ? "active" : "error"} label={j.status} /></td>
                                    <td>{j.synced_count}</td>
                                    <td className="font-mono text-[11px] text-muted-foreground">{j.duration_ms} ms</td>
                                    <td className="text-muted-foreground">{j.error || "—"}</td>
                                    <td className="font-mono text-[11px] text-muted-foreground">{j.retried_at ? relativeTime(j.retried_at) : "—"}</td>
                                    <td>
                                        {j.status === "failed" && (
                                            <button onClick={() => retry(j.id)} disabled={retrying === j.id} className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-0.5 text-[11px] hover:bg-muted/60">
                                                <RefreshCw size={10} className={retrying === j.id ? "animate-spin" : ""} /> Retry
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {jobs.length === 0 && <tr><td colSpan="8" className="py-12 text-center text-muted-foreground">No sync jobs</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}
